class Money {
  constructor(amount, currency = "USD") {
    this.currency = currency;
    this.cents = centsFromMoney(`${amount || 0}`);
    if (this.cents === 0) {
      return Money.Zero;
    }
  }
  get amount() {
    return (this.cents / 100).toFixed(2);
  }
  get inCents() {
    return this.cents.toFixed(0);
  }
  get value() {
    return +this.amount;
  }
  negate() {
    return new Money(-1 * this.value);
  }
  absolute() {
    return Money.absolute(this);
  }
  add(...addends) {
    return Money.add(this, ...addends);
  }
  subtract(...subtrahends) {
    return Money.subtract(this, ...subtrahends);
  }
  multiplyBy(multiplier) {
    return Money.multiply(this, multiplier);
  }
  equals(money) {
    return Money.equal(this, money);
  }
  isLessThan(money) {
    return Money.lessThan(this, money);
  }
  isGreaterThan(money) {
    return Money.greaterThan(this, money);
  }
  isLessThanOrEqualTo(money) {
    return Money.lessThanOrEqual(this, money);
  }
  isGreaterThanOrEqualTo(money) {
    return Money.greaterThanOrEqual(this, money);
  }
  isNegative() {
    return Money.isNegative(this);
  }
  isPositive() {
    return Money.isPositive(this);
  }
  isZero() {
    return Money.isZero(this);
  }
  toString() {
    return this.amount;
  }
  getWholeDollars() {
    return Money.getWholeDollars(this);
  }
  getFractionalCents() {
    return Money.getFractionalCents(this);
  }
}
Money.absolute = absolute;
Money.add = add;
Money.equal = equal;
Money.format = format;
Money.fromCents = fromCents;
Money.greaterThan = greaterThan;
Money.greaterThanOrEqual = greaterThanOrEqual;
Money.isEqualTo = isEqualTo;
Money.isNegative = isNegative;
Money.isPositive = isPositive;
Money.isZero = isZero;
Money.lessThan = lessThan;
Money.lessThanOrEqual = lessThanOrEqual;
Money.max = max;
Money.min = min;
Money.multiply = multiply;
Money.negate = negate;
Money.subtract = subtract;
Money.sum = sum;
Money.getWholeDollars = getWholeDollars;
Money.getFractionalCents = getFractionalCents;
Money.Zero = new Money(0);
const Zero = Money.Zero;
function centsFromMoney(money) {
  if (!money) {
    return 0;
  }
  if (typeof money === "number") {
    if (money !== money) {
      throw new Error("Cannot convert NaN to cents");
    }
    return Math.round(money * 100);
  }
  if (typeof money === "string") {
    if (+money !== +money) {
      throw new Error("Cannot convert NaN to cents: " + money);
    }
    return Math.round(+money * 100);
  }
  return +money.amount * 100;
}
function sumMonies(augend, ...summands) {
  return summands.reduce(sumMoneyAsCents, centsFromMoney(augend)) / 100;
}
function sumMoneyAsCents(_sum, value) {
  return _sum + centsFromMoney(value);
}
function format(money) {
  return new Money(money).amount;
}
function fromCents(cents) {
  return new Money(cents / 100);
}
function negate(money) {
  return new Money(money).negate();
}
function sum(augend, ...summands) {
  if (!augend) {
    return Money.Zero;
  }
  const totalAmount = sumMonies(augend, ...summands);
  return new Money(totalAmount);
}
function add(augend, ...addends) {
  const totalAmount = sumMonies(augend, ...addends);
  return new Money(totalAmount);
}
function subtract(minuend, ...subtrahends) {
  const totalAmount = sumMonies(Money.negate(minuend), ...subtrahends);
  return new Money(totalAmount).negate();
}
function multiply(multiplicand, multiplier) {
  return Money.fromCents(centsFromMoney(multiplicand) * multiplier);
}
function equal(money, comparator) {
  return centsFromMoney(money) === centsFromMoney(comparator);
}
function lessThan(money, comparator) {
  return centsFromMoney(money) < centsFromMoney(comparator);
}
function greaterThan(money, comparator) {
  return centsFromMoney(money) > centsFromMoney(comparator);
}
function lessThanOrEqual(money, comparator) {
  return Money.equal(money, comparator) || Money.lessThan(money, comparator);
}
function greaterThanOrEqual(money, comparator) {
  return Money.equal(money, comparator) || Money.greaterThan(money, comparator);
}
function isEqualTo(money) {
  return Money.equal(money, 0);
}
function isNegative(money) {
  return Money.lessThan(money, 0);
}
function isPositive(money) {
  return Money.greaterThan(money, 0);
}
function isZero(money) {
  return Money.equal(money, Zero);
}
function max(...monies) {
  if (!monies.length) {
    return Money.Zero;
  }
  return new Money(Math.max(...monies.map(money => centsFromMoney(money) / 100)));
}
function min(...monies) {
  if (!monies.length) {
    return Money.Zero;
  }
  return new Money(Math.min(...monies.map(money => centsFromMoney(money) / 100)));
}
function absolute(_money) {
  const money = new Money(_money);
  return money.isNegative() ? money.negate() : money;
}
function getWholeDollars(money) {
  if (isNegative(money)) {
    return `-${getWholeDollars(absolute(money))}`;
  }
  return `${Math.floor(new Money(money).value)}`;
}
function getFractionalCents(money) {
  if (isNegative(money)) {
    return `-${getFractionalCents(absolute(money))}`;
  }
  const cents = +Money.subtract(money, getWholeDollars(money)).inCents;
  return cents < 10 ? `0${cents}` : `${cents}`;
}
export { Money, Zero, format as formatAsMoney, fromCents as moneyFromCents, negate as negateMoney, sum as sumMoney, add as addMoney, subtract as subtractMoney, multiply as multiplyMoney, equal as equalMoney, lessThan as lessThanMoney, greaterThan as greaterThanMoney, lessThanOrEqual as lessThanOrEqualMoney, greaterThanOrEqual as greaterThanOrEqualMoney, isEqualTo as isEqualToMoney, isNegative as isNegativeMoney, isPositive as isPositiveMoney, max as maxMoney, min as minMoney, absolute as absoluteMoney, getWholeDollars, getFractionalCents };
